(() => {
  if (window.OakEaseRadioLoaded) return;
  window.OakEaseRadioLoaded = true;
  const prefix = 'oak-ease-radio-';
  const read = key => { try { return sessionStorage.getItem(prefix + key); } catch { return null; } };
  const save = (key, value) => { try { sessionStorage.setItem(prefix + key, String(value)); } catch {} };
  const safeLink = value => {
    if (typeof value !== 'string' || !value.trim()) return null;
    try { const url = new URL(value, window.location.origin); return url.protocol === 'https:' ? url.href : null; } catch { return null; }
  };
  const safeAudio = value => {
    if (!value) return null;
    const link = safeLink(value);
    if (!link) return null;
    const host = new URL(link).hostname;
    return host === window.location.hostname || host === 'cdn.shopify.com' ? link : null;
  };
  const clock = seconds => {
    const time = Math.max(0, Math.floor(Number(seconds) || 0));
    return Math.floor(time / 60) + ':' + String(time % 60).padStart(2, '0');
  };

  class OakEaseRadio {
    constructor(root) {
      this.root = root;
      this.strings = JSON.parse(root.querySelector('[data-companion-strings]').textContent);
      this.controls = [...root.querySelectorAll('[data-radio-play]')];
      this.volume = root.querySelector('[data-radio-volume]');
      this.selector = root.querySelector('[data-radio-track]');
      this.shuffleButton = root.querySelector('[data-radio-shuffle]');
      const storedVolume = read('volume');
      this.volume.value = storedVolume === null ? '12' : String(Math.max(0, Math.min(50, Number(storedVolume) || 0)));
      this.wanted = read('preference') !== 'paused';
      this.shuffle = read('shuffle') === 'true';
      this.editor = Boolean(window.Shopify?.designMode || window.Shopify?.visualPreviewMode);
      this.playlist = [];
      this.track = 0;
      this.history = [];
      this.shuffleBag = [];
      this.resumePosition = Math.max(0, Number(read('position')) || 0);
      this.attempt = 0;
      this.events = new AbortController();
      const options = {signal: this.events.signal};
      root.querySelector('.or-radio-toggle').addEventListener('click', event => {
        const panel = root.querySelector('.or-radio-panel');
        panel.hidden = !panel.hidden;
        event.currentTarget.setAttribute('aria-expanded', String(!panel.hidden));
      }, options);
      this.controls.forEach(button => button.addEventListener('click', () => this.toggle(), options));
      root.querySelector('[data-radio-next]').addEventListener('click', () => this.nextTrack(1, true), options);
      root.querySelector('[data-radio-previous]').addEventListener('click', () => this.nextTrack(-1, true), options);
      this.selector.addEventListener('change', () => this.selectTrack(Number(this.selector.value), true), options);
      this.shuffleButton.addEventListener('click', () => {
        this.shuffle = !this.shuffle;
        this.shuffleBag = [];
        save('shuffle', this.shuffle);
        this.update();
      }, options);
      this.volume.addEventListener('input', () => {
        save('volume', this.volume.value);
        if (this.audio) this.audio.volume = Number(this.volume.value) / 100;
      }, options);
      const interact = event => {
        if (!event.isTrusted || !this.wanted || this.playing || this.pending || this.loadingAudio || this.editor || this.error || document.hidden) return;
        if (event.target.closest?.('[data-radio-play], [data-radio-next], [data-radio-previous], [data-radio-track]')) return;
        if (event.type === 'keydown' && (event.repeat || event.altKey || event.ctrlKey || event.metaKey || ['Escape', 'Shift', 'Control', 'Alt', 'Meta'].includes(event.key))) return;
        this.start(true);
      };
      document.addEventListener('pointerup', interact, options);
      document.addEventListener('keydown', interact, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.releaseAudio();
        else if (this.wanted && !this.editor) this.start();
        this.update();
      }, options);
      window.addEventListener('pagehide', () => { this.away = true; this.releaseAudio(); }, options);
      window.addEventListener('pageshow', () => { this.away = false; if (this.wanted && !this.editor) this.start(); }, options);
      this.update();
      this.loadCatalog().then(loaded => { if (loaded && this.wanted && !this.editor) this.start(); });
    }

    async loadCatalog() {
      if (this.playlist.length) return true;
      if (this.catalogPromise) return this.catalogPromise;
      this.loadingCatalog = true;
      this.error = null;
      this.catalogController = new AbortController();
      const controller = this.catalogController;
      const abort = () => controller.abort();
      this.events.signal.addEventListener('abort', abort, {once: true});
      this.update();
      this.catalogPromise = (async () => {
        const timeout = setTimeout(abort, 12000);
        try {
          const url = safeLink(this.root.dataset.radioCatalogUrl);
          if (!url) throw new Error('catalog');
          const response = await fetch(url, {credentials: 'omit', signal: controller.signal});
          if (!response.ok) throw new Error('catalog');
          const body = await response.json();
          const rows = Array.isArray(body) ? body : body.tracks;
          if (!Array.isArray(rows)) throw new Error('catalog');
          const seen = new Set();
          this.playlist = rows.filter(row => {
            if (!row || typeof row.id !== 'string' || !row.id || seen.has(row.id) || typeof row.title !== 'string' || !row.title || typeof row.artist !== 'string' || !row.artist || !safeAudio(row.audioUrl) || !safeLink(row.sourceUrl) || !safeLink(row.licenseUrl) || typeof row.licenseLabel !== 'string') return false;
            seen.add(row.id);
            return true;
          }).slice(0, 30).map(row => ({...row, audioUrl: safeAudio(row.audioUrl), sourceUrl: safeLink(row.sourceUrl), licenseUrl: safeLink(row.licenseUrl)}));
          if (!this.playlist.length) throw new Error('catalog');
          if (this.destroyed) return false;
          const savedIndex = this.playlist.findIndex(track => track.id === read('track'));
          this.track = savedIndex < 0 ? 0 : savedIndex;
          if (savedIndex < 0) this.resumePosition = 0;
          this.renderPlaylist();
          this.updateTrack();
          return true;
        } catch {
          if (!this.destroyed) this.error = 'catalog';
          return false;
        } finally {
          clearTimeout(timeout);
          this.events.signal.removeEventListener('abort', abort);
          this.loadingCatalog = false;
          this.catalogPromise = null;
          if (!this.destroyed) this.update();
        }
      })();
      return this.catalogPromise;
    }

    renderPlaylist() {
      this.selector.replaceChildren();
      const credits = this.root.querySelector('[data-radio-credits]');
      credits.replaceChildren();
      this.playlist.forEach((track, index) => {
        const option = document.createElement('option');
        option.value = String(index);
        option.textContent = track.title;
        this.selector.append(option);
        const row = document.createElement('li');
        const title = document.createElement('strong');
        title.textContent = track.title + ' — ' + track.artist;
        const links = document.createElement('span');
        for (const [label, url] of [[this.strings.source, track.sourceUrl], [track.licenseLabel, track.licenseUrl]]) {
          const link = document.createElement('a');
          link.textContent = label;
          link.href = url;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          links.append(link);
        }
        row.append(title, links);
        credits.append(row);
      });
      this.selector.disabled = false;
    }

    updateTrack() {
      const track = this.playlist[this.track];
      if (!track) return;
      this.selector.value = String(this.track);
      this.root.querySelector('[data-track-title]').textContent = track.title;
      this.root.querySelector('[data-track-artist]').textContent = track.artist;
      this.root.querySelector('[data-radio-count]').textContent = (this.track + 1) + ' / ' + this.playlist.length;
      const source = this.root.querySelector('[data-radio-current-source]');
      source.href = track.sourceUrl;
      source.textContent = this.strings.source;
      const license = this.root.querySelector('[data-radio-current-license]');
      license.href = track.licenseUrl;
      license.textContent = track.licenseLabel;
      this.root.querySelector('[data-radio-current-credits]').hidden = false;
      this.updateTime();
      this.update();
    }

    updateTime() {
      const duration = Number.isFinite(this.audio?.duration) ? this.audio.duration : this.playlist[this.track]?.duration;
      const current = this.audio?.currentTime ?? this.resumePosition;
      this.root.querySelector('[data-radio-time]').textContent = clock(current) + ' / ' + clock(duration);
    }

    toggle() {
      if (this.playing || this.pending || ((this.loadingCatalog || this.loadingAudio) && this.wanted)) {
        this.wanted = false;
        save('preference', 'paused');
        this.releaseAudio();
      } else {
        this.wanted = true;
        save('preference', 'playing');
        this.start(true);
      }
    }

    createAudio() {
      if (!window.Audio) throw new Error('audio');
      const audio = new window.Audio();
      this.audio = audio;
      this.mediaEvents = new AbortController();
      const options = {signal: this.mediaEvents.signal};
      audio.preload = 'none';
      audio.volume = Number(this.volume.value) / 100;
      audio.src = this.playlist[this.track].audioUrl;
      const current = () => this.audio === audio && !this.destroyed;
      audio.addEventListener('loadedmetadata', () => {
        if (!current()) return;
        if (this.resumePosition > 0 && Number.isFinite(audio.duration)) {
          try { audio.currentTime = Math.min(this.resumePosition, Math.max(0, audio.duration - .5)); } catch {}
        }
        this.updateTime();
      }, options);
      audio.addEventListener('playing', () => {
        if (!current()) return;
        if (!this.wanted || this.away || document.hidden) { this.releaseAudio(); return; }
        this.playing = true;
        this.loadingAudio = false;
        this.blocked = false;
        this.error = null;
        this.update();
      }, options);
      audio.addEventListener('waiting', () => {
        if (!current()) return;
        this.playing = false;
        this.loadingAudio = true;
        this.update();
      }, options);
      audio.addEventListener('pause', () => {
        if (!current()) return;
        this.playing = false;
        this.update();
      }, options);
      audio.addEventListener('timeupdate', () => {
        if (!current()) return;
        this.updateTime();
        if (!this.lastSavedAt || Date.now() - this.lastSavedAt > 5000) this.persistPosition();
      }, options);
      audio.addEventListener('ended', () => { if (current() && this.wanted) this.nextTrack(1, false); }, options);
      audio.addEventListener('error', () => {
        if (!current()) return;
        this.error = 'audio';
        this.releaseAudio();
      }, options);
      return audio;
    }

    start(fromInteraction = false) {
      if (this.destroyed || !this.wanted || this.away || document.hidden || (this.editor && !fromInteraction)) return;
      if (!this.playlist.length) {
        this.loadCatalog().then(loaded => { if (loaded) this.start(fromInteraction); });
        return;
      }
      if (this.pending || this.playing) return;
      this.error = null;
      this.blocked = false;
      try {
        const audio = this.audio || this.createAudio();
        const attempt = ++this.attempt;
        this.pending = true;
        this.loadingAudio = true;
        this.update();
        Promise.resolve(audio.play()).then(() => {
          if (this.audio !== audio || this.attempt !== attempt || this.destroyed) return;
          this.pending = false;
          if (!this.wanted || document.hidden || this.away) { this.releaseAudio(); return; }
          this.playing = !audio.paused && !audio.ended && audio.readyState >= 2;
          this.loadingAudio = !this.playing;
          this.update();
        }).catch(error => {
          if (this.audio !== audio || this.attempt !== attempt || this.destroyed) return;
          this.pending = false;
          this.loadingAudio = false;
          this.playing = false;
          this.blocked = error.name === 'NotAllowedError';
          this.error = this.blocked ? null : 'audio';
          this.update();
        });
      } catch {
        this.pending = false;
        this.loadingAudio = false;
        this.error = 'audio';
        this.update();
      }
    }

    persistPosition() {
      const track = this.playlist[this.track];
      if (!track) return;
      if (this.audio && Number.isFinite(this.audio.currentTime)) this.resumePosition = this.audio.ended ? 0 : this.audio.currentTime;
      save('track', track.id);
      save('position', this.resumePosition);
      this.lastSavedAt = Date.now();
    }

    releaseAudio() {
      this.persistPosition();
      this.attempt++;
      this.playing = false;
      this.pending = false;
      this.loadingAudio = false;
      const audio = this.audio;
      this.audio = null;
      this.mediaEvents?.abort();
      if (audio) {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      }
      this.update();
    }

    update() {
      const canPause = this.playing || this.pending || ((this.loadingCatalog || this.loadingAudio) && this.wanted);
      this.controls.forEach(button => {
        button.textContent = canPause ? 'Ⅱ' : '▶';
        button.setAttribute('aria-label', canPause ? this.strings.pause : this.strings.play);
        button.setAttribute('aria-pressed', String(Boolean(this.playing)));
        button.title = canPause ? this.strings.pause : this.strings.play;
      });
      this.shuffleButton.setAttribute('aria-pressed', String(this.shuffle));
      let status = this.strings.sound_off;
      if (this.error) status = this.error === 'catalog' ? this.strings.catalog_error : this.strings.track_error;
      else if (this.loadingCatalog) status = this.strings.loading_tracks;
      else if (this.playing) status = this.strings.playing;
      else if ((document.hidden || this.away) && this.wanted) status = this.strings.background_paused;
      else if (this.loadingAudio) status = this.strings.loading_audio;
      else if (this.wanted && !this.editor) status = this.strings.autoplay_waiting;
      this.root.querySelector('[data-radio-status]').textContent = status;
      this.root.classList.toggle('is-playing', Boolean(this.playing));
    }

    selectTrack(index, fromInteraction = false, preserveShuffle = false) {
      if (!Number.isInteger(index) || !this.playlist[index]) return;
      this.releaseAudio();
      this.track = index;
      if (!preserveShuffle) { this.shuffleBag = []; this.history = []; }
      this.resumePosition = 0;
      this.error = null;
      this.persistPosition();
      this.updateTrack();
      if (this.wanted) this.start(fromInteraction);
    }

    nextTrack(direction = 1, fromInteraction = false) {
      if (!this.playlist.length) return;
      let index;
      if (direction < 0 && this.shuffle && this.history.length) index = this.history.pop();
      else if (direction > 0 && this.shuffle && this.playlist.length > 1) {
        if (!this.shuffleBag.length) {
          this.shuffleBag = this.playlist.map((_, i) => i).filter(i => i !== this.track);
          for (let i = this.shuffleBag.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this.shuffleBag[i], this.shuffleBag[j]] = [this.shuffleBag[j], this.shuffleBag[i]];
          }
        }
        index = this.shuffleBag.pop();
        this.history.push(this.track);
        if (this.history.length > 30) this.history.shift();
      } else index = (this.track + direction + this.playlist.length) % this.playlist.length;
      this.selectTrack(index, fromInteraction, true);
    }

    destroy() {
      this.destroyed = true;
      this.events.abort();
      this.releaseAudio();
      delete this.root.oakEaseRadio;
    }
  }

  const initialize = scope => scope.querySelectorAll('orderly-companion').forEach(root => {
    if (!root.oakEaseRadio && root.querySelector('[data-radio-play]')) root.oakEaseRadio = new OakEaseRadio(root);
  });
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('orderly-companion').forEach(root => root.oakEaseRadio?.destroy()));
})();
